import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Layout from './Layout/Layout';
import Dashboard from './Pages/Dashboard';
import Login from './Pages/Login';
import PrivateRoute from './Components/PrivateRoute';
import TestPage from './Pages/TestPage';
import ActivatePage from './Pages/ActivatePage';
import SetFirstPassword from './Pages/SetFirstPassword';
import ChangePassword from './Pages/ChangePassword';
import MFASetup from './Pages/MFASetup';
import DettaglioFornitore from './Pages/DettaglioFornitore';
import AddNewFornitore from './Pages/AddNewFornitore';
import CyberServeillanceFornitore from './Pages/CyberSurveillanceFornitore';
import Awareness from './Pages/Awareness';
import AwarenessRoute from './Components/AwarenessRoute';
import useGet from './Hooks/useGet';
import API_ENDPOINTS from './Config/api';

// Componente per navbarItems dinamici
function DynamicNavbarItems({ children }) {
  // Recupera i dati dell'ordine attivo (per A-Sight)
  const { fetchedData: orderData } = useGet(API_ENDPOINTS.currentOrder);

  const navbarItems = [
    { label: 'Dashboard', toLink: '/dashboard', icon: null },
    { label: 'Plugin Admin', toLink: '/pluginadmin', icon: null },
    // Aggiungi Awareness solo se abilitato nell'ordine
    ...(orderData?.features?.awareness?.enabled ? [{ label: 'Awareness', toLink: '/awareness', icon: null }] : []),
  ];

  return children(navbarItems);
}

const pageVariants = {
  none: {},
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  slide: {
    initial: { x: 100, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: -100, opacity: 0 },
  },
  slideUp: {
    initial: { y: 100, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: -100, opacity: 0 },
  },
  fadeLeft: {
    initial: { x: -100, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: -100, opacity: 0 },
  },
  fadeRight: {
    initial: { x: 100, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: 100, opacity: 0 },
  },
  fadeUp: {
    initial: { y: -100, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: -100, opacity: 0 },
  },
  fadeDown: {
    initial: { y: 100, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: 100, opacity: 0 },
  },
  zoom: {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.8, opacity: 0 },
  },
  rotate: {
    initial: { rotate: -10, opacity: 0 },
    animate: { rotate: 0, opacity: 1 },
    exit: { rotate: 10, opacity: 0 },
  },
  flip: {
    initial: { rotateY: 90, opacity: 0 },
    animate: { rotateY: 0, opacity: 1 },
    exit: { rotateY: -90, opacity: 0 },
  },
  skew: {
    initial: { skewX: '45deg', opacity: 0 },
    animate: { skewX: '0deg', opacity: 1 },
    exit: { skewX: '-45deg', opacity: 0 },
  },
  bounce: {
    initial: { y: 50, scale: 0.9, opacity: 0 },
    animate: {
      y: [50, -10, 0],
      scale: [0.9, 1.05, 1],
      opacity: 1,
    },
    exit: { opacity: 0, scale: 0.9 },
  },
  spring: {
    initial: { scale: 0.8, opacity: 0 },
    animate: {
      scale: 1,
      opacity: 1,
      transition: { type: 'spring', stiffness: 120, damping: 14 }
    },
    exit: { scale: 0.8, opacity: 0 },
  },
};

// Wrapper che applica le animazioni di entrata/uscita
const PageWrapper = ({ children, variant = 'fadeLeft' }) => {
  const v = pageVariants[variant];
  return (
    <motion.main
      initial={v.initial}
      animate={v.animate}
      exit={v.exit}
      transition={{ duration: 0.5, ease: 'easeInOut' }}
      style={{ flex: 1 }}
    >
      {children}
    </motion.main>
  );
};

// Route component per le pagine protette con animazioni
function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>

        <Route path="/dashboard" element={<PageWrapper><Dashboard /></PageWrapper>} />
        {/* <Route path="/dettagliofornitore" element={<PageWrapper><DettaglioFornitore /></PageWrapper>} /> */}
        <Route path="/addnewfornitore" element={<PageWrapper><AddNewFornitore /></PageWrapper>} />
        <Route path="/cybersurveillancefornitore/:id" element={<PageWrapper><CyberServeillanceFornitore /></PageWrapper>} />
        <Route path="/awareness" element={<PageWrapper><AwarenessRoute><Awareness /></AwarenessRoute></PageWrapper>} />

      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastContainer
        position="top-right"
        autoClose={1500}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <Routes>
        {/* Rotta pubblica */}
        <Route path="/" element={<Login />} />
        <Route path="/activate/:token" element={<ActivatePage />} />
        <Route path="/reset-password/:token" element={<SetFirstPassword />} />
        <Route path="/change-password" element={<PrivateRoute><ChangePassword /></PrivateRoute>} />
        <Route path="/mfa-setup" element={<PrivateRoute><MFASetup /></PrivateRoute>} />

        {/* Rotte protette: Layout + animazioni */}
        <Route
          element={
            <PrivateRoute>
              <DynamicNavbarItems>
                {(navbarItems) => <Layout navbarItems={navbarItems} />}
              </DynamicNavbarItems>
            </PrivateRoute>
          }
        >
          <Route path="/*" element={<AnimatedRoutes />} />
        </Route>


        {/* <Route path="/questionario/:id" element={<Questionario />} /> */}
        <Route path="/testpage" element={<TestPage />} />
        {/* <Route path="/report/:id" element={<PrivateRoute><Report /></PrivateRoute>} /> */}

      </Routes>
    </BrowserRouter>
  );
}
